import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';

import { Country } from 'common/src/constants/countries';
import { ApiError } from 'common/src/exceptions/ErrorHandling';
import { IEnergyAsset, IFacility, IPage } from 'common/src/types/interfaces';

import {
  addEnergyAsset,
  updateEnergyAsset,
} from 'portal/src/api/EnergyAssetsService';
import { getFacilities } from 'portal/src/api/FacilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { formControlErrors } from 'portal/src/components/common/forms/formControlErrors';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  defaultValues,
  getAssetInformationFields,
  getLocationDetailsFields,
  getOperationalInformationFields,
} from './EnergyAssetForm.fields';

const EnergyAssetForm = ({
  entity = {},
  handleBackAction,
}: {
  entity?: Partial<IEnergyAsset>;
  handleBackAction: () => void;
}) => {
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    setValue,
    ...formControl
  } = useForm({
    defaultValues: defaultValues(entity),
  });
  const [selectedCountry] = useWatch({ control, name: ['country'] });

  const handleError = (error: any) => {
    if (ApiError.compatible(error)) {
      formControlErrors(formControl, error);
    }

    snackbarHandler.error(MESSAGES.GENERIC.ERROR);
  };

  const { data: facilities, isFetching } = useQuery({
    queryKey: ['facilities'],
    queryFn: async () => await getFacilities(),
    initialData: { data: [] } as IPage<IFacility>,
  });

  const {
    mutate: tryUpdateEnergyAsset,
    isPending: isUpdateEnergyAssetPending,
  } = useMutation({
    mutationKey: ['updateEnergyAsset'],
    mutationFn: async (payload: Partial<IEnergyAsset>) =>
      await updateEnergyAsset(entity.id, payload),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ENERGY_ASSET.UPDATED);
      handleBackAction();
    },
    onError: handleError,
  });

  const { mutate: addEnergyAssetMutation, isPending: isAddEnergyAssetPending } =
    useMutation({
      mutationKey: ['addEnergyAsset'],
      mutationFn: async (payload: Partial<IEnergyAsset>) =>
        await addEnergyAsset({ ...payload, organizationId }),
      onSuccess: () => {
        snackbarHandler.success(MESSAGES.ENERGY_ASSET.CREATED);
        handleBackAction();
      },
      onError: handleError,
    });

  const sendData = (payload) => {
    delete payload.latitude;
    delete payload.longitude;
    delete payload.timezone;
    delete payload.vendorId;

    !entity.id
      ? addEnergyAssetMutation(payload)
      : tryUpdateEnergyAsset(payload);
  };

  const handleFacilityIdChange = (facilityId: string | null) => {
    const selectedFacility = facilities.data.find(
      (facility) => facility.id === facilityId,
    );

    if (selectedFacility) {
      setValue('address1', selectedFacility.address1 || '');
      setValue('address2', selectedFacility.address2 || '');
      setValue('city', selectedFacility.city || '');
      setValue('postalCode', selectedFacility.postalCode || '');
      setValue('state', selectedFacility.state || '');
      setValue('country', (selectedFacility.country || '') as Country);
    }
  };

  const assetInfoFieldsConfig = getAssetInformationFields();
  const locationFieldsConfig = getLocationDetailsFields(
    facilities.data,
    selectedCountry,
    handleFacilityIdChange,
  );
  const operationalInfoFieldsConfig = getOperationalInformationFields();

  return (
    <FormContainer
      control={control}
      header="Energy Asset Information"
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => sendData(data))}
      isDirty={isDirty}
      isNew={!entity.id}
      isPending={
        isAddEnergyAssetPending || isUpdateEnergyAssetPending || isFetching
      }
      mainFieldsConfig={[
        ...assetInfoFieldsConfig,
        ...locationFieldsConfig,
        ...operationalInfoFieldsConfig,
      ]}
    />
  );
};

export default EnergyAssetForm;
